// Аватар и подпись агента / человека / системы. Только монограммы на нейтральной плитке:
// эмодзи из данных (Agent.avatar) не показываем — правило Orbis «никаких эмодзи в интерфейсе».
import { currentUser } from '../auth.ts'
import { Link } from '../router.tsx'
import { useAgentMap } from '../store.ts'
import type { Agent } from '../types.ts'
import { Icon } from './Icon.tsx'
import { cx } from './ui.tsx'

const SIZES = {
  xs: 'h-[18px] w-[18px] text-[8.5px] rounded-[5px]',
  sm: 'h-[22px] w-[22px] text-[9.5px] rounded-[6px]',
  md: 'h-7 w-7 text-[11px] rounded-[7px]',
  lg: 'h-10 w-10 text-[14px] rounded-[9px]',
  xl: 'h-14 w-14 text-[19px] rounded-[12px]',
} as const
export type AvatarSize = keyof typeof SIZES

const STOP = new Set(['лид', 'агент', 'сервис', 'внешний', 'the', 'и', 'of'])

export function monogram(name: string): string {
  const words = name
    .replace(/[^\p{L}\p{N}\s\-_.]/gu, ' ')
    .split(/[\s\-_.]+/)
    .filter(Boolean)
  const meaningful = words.filter((w) => !STOP.has(w.toLowerCase()))
  const list = meaningful.length ? meaningful : words
  if (!list.length) return '?'
  if (list.length === 1) return list[0]!.slice(0, 2).toUpperCase()
  return (list[0]![0]! + list[1]![0]!).toUpperCase()
}

/** Буквы из Agent.avatar, если это 1–2 буквы/цифры; эмодзи и прочее игнорируются. */
function avatarLetters(a: Agent | null | undefined, fallbackName?: string): string {
  const raw = a?.avatar?.trim() ?? ''
  if (/^[\p{L}\p{N}]{1,2}$/u.test(raw)) return raw.toUpperCase()
  return monogram(a?.name ?? fallbackName ?? '?')
}

export function AgentAvatar({
  agent,
  size = 'sm',
  className,
  fallbackName,
}: {
  agent: Agent | null | undefined
  size?: AvatarSize
  className?: string
  fallbackName?: string
}) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        'inline-flex shrink-0 items-center justify-center border border-line bg-surface-2 font-semibold leading-none tracking-[0.02em] text-fg-2 select-none',
        SIZES[size],
        className,
      )}
    >
      {avatarLetters(agent, fallbackName)}
    </span>
  )
}

/** Аватар + имя агента по id (из живого справочника). */
export function AgentLabel({
  id,
  size = 'sm',
  link = false,
  className,
  muted,
}: {
  id: string | null | undefined
  size?: AvatarSize
  link?: boolean
  className?: string
  muted?: boolean
}) {
  const agents = useAgentMap()
  if (!id) return <span className={cx('text-subtle', className)}>—</span>
  const a = agents.get(id)
  const body = (
    <>
      <AgentAvatar agent={a} size={size} fallbackName={id} />
      <span className={cx('truncate', muted ? 'text-muted' : undefined)}>{a?.name ?? id}</span>
    </>
  )
  // не шире контейнера (длинное имя обрезается), если вызывающий не задал свою максимальную ширину
  const cls = cx('inline-flex min-w-0 items-center gap-1.5', !/(^|\s)max-w-/.test(className ?? '') && 'max-w-full', className)
  return link ? (
    <Link to={`/agents/${encodeURIComponent(id)}`} className={cx(cls, 'hover:underline')} title={a?.title || a?.name || id}>
      {body}
    </Link>
  ) : (
    <span className={cls} title={a?.title || a?.name || id}>
      {body}
    </span>
  )
}

export function humanLabel(id: string): string {
  const email = id.replace(/^human:/, '')
  const me = currentUser()
  if (me && (me.email === email || String(me.id) === email)) return 'Ты'
  return email || 'Человек'
}

/** Автор вида human:<email> | agent:<id> | system, либо пара (тип, id). */
export function ActorLabel({
  type,
  id,
  size = 'sm',
  link,
}: {
  type: 'agent' | 'human' | 'system' | 'runner' | string
  id: string
  size?: AvatarSize
  link?: boolean
}) {
  if (type === 'agent') return <AgentLabel id={id} size={size} link={link} />
  const isHuman = type === 'human'
  const label = isHuman ? humanLabel(id) : type === 'runner' ? `раннер ${id}` : 'Система'
  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
      <span
        aria-hidden="true"
        className={cx(
          'inline-flex shrink-0 items-center justify-center border font-semibold leading-none',
          SIZES[size],
          isHuman ? 'border-line bg-surface-3 text-fg-2' : 'border-line bg-surface-2 text-muted',
        )}
      >
        {isHuman ? (
          label === 'Ты' ? (
            'Я'
          ) : (
            monogram(label.split('@')[0] ?? label)
          )
        ) : type === 'runner' ? (
          <Icon name="server" size={size === 'xs' ? 10 : 12} />
        ) : (
          <Icon name="settings" size={size === 'xs' ? 10 : 12} />
        )}
      </span>
      <span className="truncate">{label}</span>
    </span>
  )
}

export function CreatedBy({ value, size = 'xs' }: { value: string; size?: AvatarSize }) {
  if (value.startsWith('agent:')) return <AgentLabel id={value.slice(6)} size={size} link />
  if (value.startsWith('human:')) return <ActorLabel type="human" id={value} size={size} />
  return <ActorLabel type="system" id={value} size={size} />
}
